import React, { useEffect, useRef, useState, useMemo } from "react";
import { flushSync } from "react-dom";
import {
  Modal,
  Text,
  Box,
  Avatar,
  Table,
  createStyles,
  ActionIcon,
  Flex,
} from "@mantine/core";
import TableLoader from "../../../components/TableLoader";
import Cuations from "./Cuations";
import { useSelector } from "react-redux";
import { getMar } from "../../../services";
import moment from "moment";
import {
  decrypt,
  getSpecificMedicationStatus,
  getCookie,
  getActiveSlotForDate,
  getActiveSlotsForDate,
} from "../../../utils";
import { baseURL } from "../../../http";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import defaultImage from "./../../../images/personal2.png";
import { getAllSignatures } from "../../../services/signature";
import { getAllPatients } from "../../../services/patient";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import {
  ReportHeader,
  ReportMetaGrid,
  ReportFooter,
  S as BrandS,
  BRAND,
  PDF_WIDTH,
} from "../../../components/BrandedReport";

const getMedicationStatusHelper = (item) => {
  if (!item) return "";
  return getSpecificMedicationStatus(
    item?.medicationType,
    item?.date,
    item?.timeSlot,
  );
};

const SignatureInitialsImage = ({
  url,
  isPDF,
  item,
  fallbackText,
  fallbackInitials,
  pdfImgStyle,
}) => {
  // For PDF export we need a base64 src (canvas reads pixel data).
  // For normal display we render the URL directly — no crossOrigin needed,
  // and CSS filter handles the invert case, so CORS is never triggered.
  const [pdfSrc, setPdfSrc] = useState(null);
  const [imgError, setImgError] = useState(false);

  const isOutOfMed = getMedicationStatusHelper(item) === "Out of Medication";

  // Only attempt canvas conversion when rendering into the PDF hidden div.
  useEffect(() => {
    if (!isPDF || !url) return;
    let mounted = true;
    setPdfSrc(null);

    // Data URLs are already base64 — no network fetch needed.
    // Avoid crossOrigin on data URLs; some html2canvas versions try to
    // re-fetch them with CORS mode which always fails.
    if (url.startsWith("data:")) {
      if (!isOutOfMed) {
        // Use as-is — no inversion needed.
        if (mounted) setPdfSrc(url);
        return () => {
          mounted = false;
        };
      }
      // Need to invert pixels for "Out of Medication" cells.
      const img = new Image();
      img.onload = () => {
        if (!mounted) return;
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          for (let i = 0; i < imgData.data.length; i += 4) {
            imgData.data[i] = 255 - imgData.data[i];
            imgData.data[i + 1] = 255 - imgData.data[i + 1];
            imgData.data[i + 2] = 255 - imgData.data[i + 2];
          }
          ctx.putImageData(imgData, 0, 0);
          if (mounted) setPdfSrc(canvas.toDataURL("image/png"));
        } catch (e) {
          if (mounted) setPdfSrc(url);
        }
      };
      img.onerror = () => {
        if (mounted) setPdfSrc(url);
      };
      img.src = url;
      return () => {
        mounted = false;
      };
    }

    // External URL (S3) — load via canvas with CORS to get a clean base64.
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (!mounted) return;
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        if (isOutOfMed) {
          const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          for (let i = 0; i < imgData.data.length; i += 4) {
            imgData.data[i] = 255 - imgData.data[i];
            imgData.data[i + 1] = 255 - imgData.data[i + 1];
            imgData.data[i + 2] = 255 - imgData.data[i + 2];
          }
          ctx.putImageData(imgData, 0, 0);
        }

        if (mounted) setPdfSrc(canvas.toDataURL("image/png"));
      } catch (e) {
        // Canvas tainted (CORS not configured on S3) — fall back to raw URL.
        if (mounted) setPdfSrc(url);
      }
    };
    img.onerror = () => {
      if (mounted) setPdfSrc(url);
    };
    img.src = url;

    return () => {
      mounted = false;
    };
  }, [url, isPDF, isOutOfMed]);

  // ── PDF mode ──────────────────────────────────────────────────────────────
  if (isPDF) {
    // CRITICAL: html2canvas captures the DOM synchronously.  If we render a raw
    // S3 URL (even temporarily while pdfSrc is loading), html2canvas will attempt
    // a CORS-gated fetch on that URL and fail with a CORS error — the image ends
    // up blank in the downloaded PDF.
    //
    // Rule: in PDF mode, ONLY render an <img> when we have a confirmed data: URL
    // (either the url prop is already a data: URL, or pdfSrc has been resolved).
    // For anything else, show the text/initials fallback — never a raw S3 URL.
    if (!url) {
      if (fallbackText) {
        return (
          <Text fz={20} fw={400} ff="Island Moments" color="#171717">
            {fallbackText}
          </Text>
        );
      }
      if (fallbackInitials) return <span>{fallbackInitials}</span>;
      return null;
    }

    // If url is already a data: URL, use it directly.
    if (url.startsWith("data:")) {
      const displaySrc = pdfSrc || url; // pdfSrc may have inverted pixels for OutOfMed
      return (
        <img
          src={displaySrc}
          alt="initials-img"
          style={
            pdfImgStyle ?? {
              width: "100px",
              height: "auto",
              display: "block",
              objectFit: "contain",
            }
          }
        />
      );
    }

    // url is a raw S3 URL.  Only render once pdfSrc (the base64 conversion) is ready.
    // While it's still loading, show text/initials fallback — NEVER the raw S3 URL,
    // because html2canvas may capture the DOM at any moment during the await chain.
    if (!pdfSrc) {
      if (fallbackText) {
        return (
          <Text fz={20} fw={400} ff="Island Moments" color="#171717">
            {fallbackText}
          </Text>
        );
      }
      if (fallbackInitials) return <span>{fallbackInitials}</span>;
      return null;
    }

    // pdfSrc is ready (base64) — render it. No crossOrigin needed on data: URLs.
    return (
      <img
        src={pdfSrc}
        alt="initials-img"
        style={
          pdfImgStyle ?? {
            width: "100px",
            height: "auto",
            display: "block",
            objectFit: "contain",
          }
        }
      />
    );
  }

  // ── Normal display mode ───────────────────────────────────────────────────
  // Render the S3 URL directly. No crossOrigin attribute = browser loads it
  // as a normal image (CORS headers not required for display-only).
  // CSS filter handles the invert without touching canvas pixels.
  if (!url || imgError) {
    if (fallbackText) {
      return (
        <Text fz={20} fw={400} ff="Island Moments" color="#171717">
          {fallbackText}
        </Text>
      );
    }
    if (fallbackInitials) return <span>{fallbackInitials}</span>;
    return null;
  }

  return (
    <img
      src={url}
      alt="initials-img"
      onError={() => setImgError(true)}
      style={{
        width: "100px",
        height: "auto",
        display: "block",
        objectFit: "contain",
        ...(isOutOfMed && { filter: "invert(100%)" }),
      }}
    />
  );
};

const MarViewReportModal = ({
  opened,
  onClose,
  selectedClient,
  monthValue,
}) => {
  const { classes } = useStyles();
  const { mar: appointments, loading } = useSelector((state) => state.mar);
  const [marTableData, setMarTableData] = useState([]);
  const safeMonthValue = Array.isArray(monthValue)
    ? monthValue.filter(Boolean)
    : [];
  const safeClient = selectedClient || {};
  const safeForm1 = safeClient?.form1 || {};
  const image = decrypt(selectedClient?.imageurl);
  const [staffList, setStaffList] = useState([]);
  const [pdfSignatures, setPdfSignatures] = useState(null);
  const { allSignature } = useSelector((state) => state?.signature);
  const { profile } = useSelector((state) => state?.profile);
  const [enhancedAppointments, setEnhancedAppointments] = useState([]);
  const [pdfLoading, setPDFLoading] = useState(false);
  const allFacilities = useSelector((state) => state?.facility);
  const facilityName =
    safeClient?.facility?.name ||
    allFacilities?.facilities?.find((f) => f.id === safeClient?.facilityId)
      ?.name ||
    profile?.facility?.name ||
    "Facility";
  const facilityPhoto = decrypt(
    safeClient?.facility?.imageurl ||
      allFacilities?.facilities?.find((f) => f.id === safeClient?.facilityId)
        ?.imageurl ||
      profile?.facility?.imageurl,
  );
  const canRenderReport = Boolean(
    opened && safeClient?.id && safeMonthValue?.length,
  );

  // Memoized date array for the selected month range
  const datesArray = useMemo(() => {
    if (!monthValue || monthValue.length === 0) return [];
    const start = moment(monthValue[0]).clone().startOf("day");
    let end;
    if (
      moment(monthValue[0]).format("MMM") ===
      moment(monthValue[monthValue.length - 1]).format("MMM")
    ) {
      end = moment(monthValue[monthValue.length - 1])
        .add(1, "M")
        .clone()
        .startOf("day");
    } else {
      end = moment(monthValue[monthValue.length - 1])
        .endOf("month")
        .clone()
        .startOf("day");
    }
    const dates = [];
    let current = start.clone();
    if (
      moment(monthValue[0]).format("MMM") ===
      moment(monthValue[monthValue.length - 1]).format("MMM")
    ) {
      while (current < end) {
        dates.push(current.clone());
        current.add(1, "day");
      }
    } else {
      while (current <= end) {
        dates.push(current.clone());
        current.add(1, "day");
      }
    }
    return dates;
  }, [monthValue]);

  // Generate virtual missed entries for past dates without a record
  useEffect(() => {
    if (!opened || !appointments || !selectedClient || !datesArray.length)
      return;

    const virtualMissed = [];
    const medications = selectedClient?.form3 || [];
    const existingAppointments = appointments || [];

    // With the new schema one document covers all dates; expand each real record
    // into individual per-date entries (one per medicationType array item) so
    // existingKeys correctly reflects which specific date+slot combos are recorded.
    const expandedAppointments = [];
    existingAppointments.forEach((apt) => {
      const isVirtual = String(apt._id || "").startsWith("virtual_");
      if (
        !isVirtual &&
        Array.isArray(apt.medicationType) &&
        apt.medicationType.length > 0
      ) {
        apt.medicationType.forEach((mtEntry) => {
          const slot = mtEntry.timeSlot || getActiveSlotForDate(apt.timeSlot, mtEntry.date);
          expandedAppointments.push({
            ...apt,
            date: mtEntry.date,
            timeSlot: slot,
            medicationType: [mtEntry],
            staff: mtEntry.staff || apt.staff,
            comments: mtEntry.comments || apt.comments,
            administeredAt: mtEntry.administeredAt || null,
          });
        });
      } else {
        expandedAppointments.push(apt);
      }
    });

    const existingKeys = new Set();
    expandedAppointments.forEach((apt) => {
      const dateStr = moment(apt.date).format("YYYY-MM-DD");
      const slots = Array.isArray(apt.timeSlot)
        ? apt.timeSlot.map((s) => s?.time).filter(Boolean)
        : [apt.timeSlot].filter(Boolean);
      slots.forEach((slot) => {
        existingKeys.add(`${apt.medicationId}_${dateStr}_${slot}`);
      });
    });

    // Build map of medicationId → raw timeSlot history (new-format objects with effectiveFrom/effectiveTo)
    const medTimeSlotMap = new Map();
    existingAppointments.forEach((entry) => {
      const medId = entry?.medicationId;
      if (
        medId &&
        Array.isArray(entry?.timeSlot) &&
        entry.timeSlot.some((s) => s && typeof s === "object") &&
        !medTimeSlotMap.has(medId)
      ) {
        medTimeSlotMap.set(medId, entry.timeSlot);
      }
    });

    const today = moment().startOf("day");

    medications.forEach((med) => {
      const medType = med.medicationType;
      let isPRN = false;
      let isDiscontinued = false;
      if (typeof medType === "string") {
        isPRN = medType.toUpperCase() === "PRN";
        isDiscontinued = medType.toUpperCase() === "DISCONTINUED";
      } else if (Array.isArray(medType) && medType.length > 0) {
        const last = medType[medType.length - 1];
        if (last?.status) {
          isPRN = last.status.toUpperCase() === "PRN";
          isDiscontinued = last.status.toUpperCase() === "DISCONTINUED";
        }
      }
      if (isPRN || isDiscontinued || med.discontinued) return;

      const medStart = moment(med.medicationDate).startOf("day");
      const medEnd =
        med.medicationEndDate && med.medicationEndDate !== "null"
          ? moment(med.medicationEndDate).endOf("day")
          : moment(datesArray[datesArray.length - 1]).endOf("day");

      let timeSlots =
        Array.isArray(med.timeSlots) && med.timeSlots.length > 0
          ? med.timeSlots
          : [];
      if (timeSlots.length === 0 && med.timeSlot) {
        timeSlots = Array.isArray(med.timeSlot)
          ? med.timeSlot.map((s) => s?.time ?? s).filter(Boolean)
          : [med.timeSlot];
      }
      if (timeSlots.length === 0) return;

      const scheduleType = med.scheduleType || "daily";

      datesArray.forEach((dateMoment) => {
        if (
          dateMoment.isBefore(medStart, "day") ||
          dateMoment.isAfter(medEnd, "day")
        )
          return;

        let shouldAdminister = false;

        if (scheduleType === "daily") {
          shouldAdminister = true;
        } else if (scheduleType === "custom") {
          const repeatType = (med.repeatType || "day").toLowerCase();
          const repeatEvery = Number(med.repeatEvery) || 1;

          if (repeatType === "day") {
            const daysSinceStart = dateMoment.diff(medStart, "days");
            shouldAdminister =
              daysSinceStart >= 0 && daysSinceStart % repeatEvery === 0;
          } else if (repeatType === "week") {
            const dayNames = [
              "Sunday",
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
            ];
            const repeatOn =
              Array.isArray(med.repeatOn) && med.repeatOn.length > 0
                ? med.repeatOn
                : [dayNames[medStart.day()]];

            const weekDiff = dateMoment.diff(
              medStart.clone().startOf("week"),
              "weeks",
            );
            if (weekDiff % repeatEvery === 0) {
              const dayName = dayNames[dateMoment.day()];
              shouldAdminister = repeatOn.includes(dayName);
            }
          } else if (repeatType === "month") {
            const monthDiff =
              (dateMoment.year() - medStart.year()) * 12 +
              (dateMoment.month() - medStart.month());
            shouldAdminister =
              monthDiff >= 0 &&
              monthDiff % repeatEvery === 0 &&
              dateMoment.date() === medStart.date();
          }
        }

        if (!shouldAdminister) return;

        const rawHistory = medTimeSlotMap.get(med.id);
        const activeSlots = rawHistory
          ? getActiveSlotsForDate(rawHistory, dateMoment.toISOString())
          : timeSlots;
        activeSlots.forEach((slot) => {
          const key = `${med.id}_${dateMoment.format("YYYY-MM-DD")}_${slot}`;
          if (existingKeys.has(key)) return;

          if (dateMoment.isBefore(today, "day")) {
            virtualMissed.push({
              _id: `virtual_${key}`,
              date: dateMoment.toISOString(),
              timeSlot: slot,
              medicationId: med.id,
              medicationType: [
                { status: "Missed", date: dateMoment.toISOString() },
              ],
              form3: med,
            });
          }
        });
      });
    });

    setEnhancedAppointments([...virtualMissed, ...expandedAppointments]);
  }, [opened, appointments, selectedClient, datesArray]);

  // Build staff list (unchanged)
  useEffect(() => {
    return;
    let tempStaffList = [];
    let _staffList = [];
    marTableData?.map((_m) => {
      _m?.data?.map((_d) => {
        let notNullMed = _d?.appointment?.filter((item) => item !== null);

        notNullMed?.map((item) => {
          const status = getMedicationStatusHelper(item);
          if (item?.staff && status?.toLowerCase() !== "missed") {
            let splittedName = item?.staff?.name?.split(" ");
            if (splittedName?.[1]) {
              tempStaffList.push({
                name: item?.staff?.name,
                initial: splittedName[0]?.[0] + splittedName[1]?.[0],
                id: item?.staff?._id?.toString() || item?.staff?.id,
              });
            } else {
              tempStaffList.push({
                name: item?.staff?.name,
                initial: splittedName[0]?.[0],
                id: item?.staff?._id?.toString() || item?.staff?.id,
              });
            }
          } else {
            return "-";
          }
        });

        tempStaffList = tempStaffList.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t?.name === item?.name),
        );

        _staffList = tempStaffList;

        tempStaffList?.map((_s) => {
          const staffIndex = staffList.findIndex((v) => v?.name === _s?.name);

          if (staffIndex !== -1) {
            _staffList?.filter((v) => v?.name !== _s?.name);
          }
        });
      });
    });

    setStaffList(_staffList);
  }, [marTableData]);

  const today = new Date();
  const formattedDate = `${today.getMonth() + 1}-${today.getDate()}-${today.getFullYear()}`;
  const reportName = `${formattedDate}_eMAR_Report`;

  // Fetch MAR data when modal opens (unchanged)
  useEffect(() => {
    if (opened) {
      getAllSignatures();
      getAllPatients("all");
      if (
        moment(safeMonthValue?.[0])?.format("MMM") ===
        moment(safeMonthValue?.[safeMonthValue?.length - 1])?.format("MMM")
      ) {
        let endVal = moment(safeMonthValue?.[safeMonthValue?.length - 1]).add(
          1,
          "M",
        );
        getMar(`monthly_${safeMonthValue[0]}_${endVal}`, safeClient?.id);
      } else {
        let endVal = moment(safeMonthValue?.[safeMonthValue?.length - 1]).endOf(
          "month",
        );
        getMar(`monthly_${safeMonthValue[0]}_${endVal}`, safeClient?.id);
      }
    }
  }, [opened]);

  useEffect(() => {
    return;
    if (canRenderReport) {
      if (
        moment(safeMonthValue?.[0])?.format("MMM") ==
        moment(safeMonthValue?.[safeMonthValue?.length - 1])?.format("MMM")
      ) {
        let endVal = moment(safeMonthValue?.[safeMonthValue?.length - 1]).add(
          1,
          "M",
        );
        getMar(`monthly_${safeMonthValue[0]}_${endVal}`, safeClient?.id);
      } else {
        let endVal = moment(safeMonthValue?.[safeMonthValue?.length - 1]).endOf(
          "month",
        );
        getMar(`monthly_${safeMonthValue[0]}_${endVal}`, safeClient?.id);
      }
    }
  }, [canRenderReport]);

  // ---------- NEW SIMPLIFIED TABLE BUILDING LOGIC ----------
  useEffect(() => {
    if (!opened || !enhancedAppointments?.length || !datesArray.length) return;

    // Define slot ranges and a helper to map a time string to a range
    const slotRanges = [
      { label: "6AM - 10AM", start: 6, end: 10 },
      { label: "10AM - 2PM", start: 10, end: 14 },
      { label: "2PM - 6PM", start: 14, end: 18 },
      { label: "6PM - 10PM", start: 18, end: 22 },
      { label: "10PM - 2AM", start: 22, end: 26 }, // 2AM next day
      { label: "2AM - 6AM", start: 2, end: 6 },
    ];

    const getSlotLabelFromTime = (timeStr) => {
      const tStr = Array.isArray(timeStr) ? timeStr[0] : timeStr;
      if (typeof tStr !== "string") return null;
      const match = tStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (!match) return null;
      let hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      const period = match[3].toUpperCase();
      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;
      const totalMinutes = hours * 60 + minutes;

      for (const range of slotRanges) {
        let startMinutes = range.start * 60;
        let endMinutes = range.end * 60;
        if (range.end <= range.start) {
          endMinutes += 24 * 60;
        }
        let checkMinutes = totalMinutes;
        if (range.label === "10PM - 2AM" && totalMinutes < 120) {
          checkMinutes += 24 * 60;
        }
        if (checkMinutes >= startMinutes && checkMinutes < endMinutes) {
          return range.label;
        }
      }
      return null;
    };

    // Build a map: medicationId -> medication info
    const medMap = {};
    enhancedAppointments.forEach((apt) => {
      const med = apt.form3;
      if (med && !medMap[med.id]) {
        medMap[med.id] = {
          id: med.id,
          name: med.medicationName,
          dosage: med.dosage,
          unit: med.unit,
          ingestionMode: med.ingestionMode,
          instruction: med.medicationInstruction,
        };
      }
    });

    // PRN medications generate no virtual missed entries so they may be absent
    // from medMap.  Look them up from selectedClient.form3 using the real DB records.
    (appointments || []).forEach((apt) => {
      if (!medMap[apt.medicationId]) {
        const medInfo = (selectedClient?.form3 || []).find(
          (m) => Number(m.id) === Number(apt.medicationId),
        );
        if (medInfo) {
          medMap[apt.medicationId] = {
            id: medInfo.id,
            name: medInfo.medicationName,
            dosage: medInfo.dosage,
            unit: medInfo.unit,
            ingestionMode: medInfo.ingestionMode,
            instruction: medInfo.medicationInstruction,
          };
        }
      }
    });

    // Group appointments by (medicationId, date, slotLabel)
    const groups = {};

    enhancedAppointments.forEach((apt) => {
      const medId = apt.medicationId;
      if (!medId) return;
      const dateStr = moment(apt.date).format("YYYY-MM-DD");
      let slotLabel = null;
      if (apt.timeSlot) {
        slotLabel = getSlotLabelFromTime(apt.timeSlot);
      } else {
        // PRN: derive from the date
        const hour = moment(apt.date).hour();
        const range = slotRanges.find((r) => {
          if (r.end <= r.start) {
            return hour >= r.start || hour < r.end;
          } else {
            return hour >= r.start && hour < r.end;
          }
        });
        slotLabel = range?.label;
      }
      if (!slotLabel) return;

      const key = `${medId}|${dateStr}|${slotLabel}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(apt);
    });

    // Build marTableData: array of { medicine, slot, data: [{ appointment, date }] }
    const newTableData = [];

    Object.keys(medMap).forEach((medId) => {
      const medicine = medMap[medId];
      slotRanges.forEach((range) => {
        const slotLabel = range.label;
        const dataForSlot = [];

        datesArray.forEach((dateMoment) => {
          const dateStr = dateMoment.format("YYYY-MM-DD");
          const key = `${medId}|${dateStr}|${slotLabel}`;
          const appointmentsInGroup = groups[key] || [];
          const validApps = appointmentsInGroup.filter((a) => a !== null);
          dataForSlot.push({
            appointment: validApps.length ? validApps : [null],
            date: dateStr,
          });
        });

        newTableData.push({
          medicine,
          slot: slotLabel,
          data: dataForSlot,
        });
      });
    });

    setMarTableData(newTableData);
  }, [opened, enhancedAppointments, datesArray]);
  // ---------------------------------------------------------

  // Helper for staff letters (unchanged)
  const returnStaffLetters = (medications, isPDF, excelExportTable = false) => {
    const notNullMed = (medications || []).filter((item) => item !== null);

    const seen = new Set();
    const uniqueMeds = [];
    for (const m of notNullMed) {
      if (!m?._id || seen.has(m._id)) continue;
      seen.add(m._id);
      uniqueMeds.push(m);
    }

    const nodes = uniqueMeds.map((item, index) => {
      const status = getMedicationStatusHelper(item);
      if (
        item?.staff &&
        status?.toLowerCase() !== "missed" &&
        status?.toLowerCase() !== "discontinued"
      ) {
        const staffId = item?.staff?.id || item?.staff?._id || item?.admin;
        const staffSignature = allSignature?.find((sig) => {
          const sigStaffId =
            sig.staff?._id?.toString() ||
            sig.staff?.id ||
            (typeof sig.staff === "string" ? sig.staff : null);
          const sigAdminId =
            sig.admin?._id?.toString() ||
            sig.admin?.id ||
            (typeof sig.admin === "string" ? sig.admin : null);
          return (
            sigStaffId === staffId?.toString() ||
            sigAdminId === staffId?.toString()
          );
        });

        const splittedName = item?.staff?.name?.split(" ");
        const initials = splittedName?.[1]
          ? splittedName[0]?.[0] + splittedName[1]?.[0]
          : splittedName?.[0]?.[0] || "";

        if (
          excelExportTable === false &&
          decrypt(staffSignature?.signatureInitials)
        ) {
          // For PDF: ONLY use the pre-fetched base64 data URL from pdfSignatures.
          // Never fall back to the raw S3 URL when isPDF=true — html2canvas will
          // attempt a CORS-gated fetch on any raw S3 URL and fail with a CORS error.
          // If pdfSignatures hasn't been populated yet (shouldn't happen since
          // flushSync guarantees it runs before html2canvas), fall back to text/
          // initials so the PDF still shows something, but never passes an S3 URL.
          if (isPDF) {
            const pdfInitialsUrl =
              pdfSignatures?.[staffId?.toString()]?.initialsUrl;
            const pdfInitialsText =
              pdfSignatures?.[staffId?.toString()]?.initialsText ||
              staffSignature?.signatureInitialsText;
            return (
              <SignatureInitialsImage
                key={staffId}
                url={pdfInitialsUrl || null}
                isPDF={true}
                item={item}
                fallbackText={pdfInitialsText}
                fallbackInitials={initials}
              />
            );
          }
          // Normal display mode — raw S3 URL is fine (no CORS needed for <img> display).
          const initialsUrl = decrypt(staffSignature?.signatureInitials);
          return (
            <SignatureInitialsImage
              key={staffId}
              url={initialsUrl}
              isPDF={false}
              item={item}
              fallbackText={staffSignature?.signatureInitialsText}
              fallbackInitials={initials}
            />
          );
        } else if (
          excelExportTable === false &&
          staffSignature?.signatureInitialsText
        ) {
          return (
            <Text fz={20} fw={400} ff="Island Moments" color="#171717">
              {staffSignature?.signatureInitialsText}
            </Text>
          );
        }

        return (
          <span key={staffId + "-init-" + index} style={{ marginRight: 6 }}>
            {initials}
          </span>
        );
      } else {
        return (
          <span key={"dash-" + index} style={{ marginRight: 6 }}>
            -
          </span>
        );
      }
    });

    return nodes;
  };

  // medicationStatus: each element is a full MAR record (with .date, .timeSlot,
  // .medicationType array).  getMedicationStatusHelper must receive the whole
  // record so getSpecificMedicationStatus can match by date+slot.
  const medicationStatus = (medications) => {
    if (medications?.every((element) => element == null)) {
      return false;
    }

    let notNullMed = medications?.filter((item) => item !== null);

    if (
      notNullMed?.every(
        (element) =>
          getMedicationStatusHelper(element)?.toLowerCase() == "missed",
      )
    ) {
      return "missed";
    }

    if (
      notNullMed?.some(
        (element) =>
          getMedicationStatusHelper(element)?.toLowerCase() == "taken",
      )
    ) {
      return "taken";
    }

    if (
      notNullMed?.every(
        (element) =>
          getMedicationStatusHelper(element)?.toLowerCase() == "refused",
      )
    ) {
      return "refused";
    }

    if (
      notNullMed?.every((element) => {
        const status = getMedicationStatusHelper(element)?.toLowerCase();
        return status == "out of medication" || status == "out";
      })
    ) {
      return "out";
    }

    if (
      notNullMed?.every(
        (element) =>
          getMedicationStatusHelper(element)?.toLowerCase() == "unable to take",
      )
    ) {
      return "unable";
    }

    if (
      notNullMed?.filter(
        (element) =>
          getMedicationStatusHelper(element)?.toLowerCase() == "missed",
      )?.length
    ) {
      return "missed";
    }

    if (
      notNullMed?.filter(
        (element) =>
          getMedicationStatusHelper(element)?.toLowerCase() == "discontinued",
      )?.length
    ) {
      return "discontinued";
    }

    if (
      notNullMed?.filter(
        (element) => getMedicationStatusHelper(element)?.toLowerCase() == "prn",
      )?.length
    ) {
      return "prn";
    }
  };

  // const today = new Date();
  // const formattedDate = `${today.getMonth() + 1}-${today.getDate()}-${today.getFullYear()}`;
  // const reportName = `${formattedDate}_eMAR_Report`;

  const downloadPDF = async () => {
    // Fetch an S3 image through the backend proxy and return it as a base64
    // data URL.  Direct fetch from the browser is blocked by S3's CORS policy;
    // routing through the backend avoids that restriction entirely.
    const toBase64 = async (url) => {
      if (!url) return null;
      if (url.startsWith("data:")) return url;
      try {
        const token = getCookie("token");
        const response = await fetch(
          `${baseURL}/mar/proxy-image?url=${encodeURIComponent(url)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok) return url;
        const blob = await response.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => resolve(url);
          reader.readAsDataURL(blob);
        });
      } catch {
        return url;
      }
    };

    // Pre-convert ALL signature images to base64 before touching the DOM.
    // This means PDFMarTableComponent receives data: URLs which:
    //  • never trigger CORS failures in html2canvas
    //  • are handled synchronously by SignatureInitialsImage (no useEffect delay)
    const sigEntries = await Promise.all(
      (staffList || []).map(async (_s) => {
        const sigObj = allSignature?.find(({ staff, admin }) => {
          const sId =
            staff?._id?.toString() ||
            staff?.id ||
            (typeof staff === "string" ? staff : null);
          const aId =
            admin?._id?.toString() ||
            admin?.id ||
            (typeof admin === "string" ? admin : null);
          return sId === _s.id || aId === _s.id;
        });

        const initialsRaw = decrypt(sigObj?.signatureInitials);
        const signRaw =
          decrypt(sigObj?.signature) || decrypt(sigObj?.signatureImage);

        const [initialsUrl, signUrl] = await Promise.all([
          toBase64(initialsRaw),
          toBase64(signRaw),
        ]);

        return [
          _s.id,
          {
            initialsUrl,
            signUrl,
            initialsText: sigObj?.signatureInitialsText,
            signText: sigObj?.signatureText,
            initial: _s.initial,
          },
        ];
      }),
    );

    const sigData = Object.fromEntries(sigEntries);

    // flushSync forces React to commit DOM updates synchronously before
    // html2canvas runs, ensuring signature images are in the DOM.
    flushSync(() => {
      setPdfSignatures(sigData);
      setPDFLoading(true);
    });

    const input = document.getElementById("marPDFReport");

    const decryptedImageUrl = `${decrypt(
      selectedClient?.imageurl,
    )}?r=${Math.floor(Math.random() * 100000)}`;

    const imageUrlCheck = decrypt(selectedClient?.imageurl);
    const defaultUrl = "/assets/images/personal.png";
    const img = new Image();
    if (imageUrlCheck == undefined) {
      img.src = defaultUrl;
    } else {
      img.src = decryptedImageUrl;
    }
    img.crossOrigin = "anonymous";

    img.onload = async function () {
      if (imageUrlCheck == undefined) {
        await createPDF(input, defaultUrl);
      } else {
        await createPDF(input, decryptedImageUrl);
      }
    };
    img.onerror = function () {
      setPDFLoading(false);
    };
  };

  const createPDF = async (input, image) => {
    const dummyImage = "/assets/images/personal.png";
    const loadImage = (src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL("image/png");
            resolve(dataURL);
          } catch (e) {
            resolve(dummyImage);
          }
        };
        img.onerror = () => {
          resolve(dummyImage);
        };
        img.src = src;
      });
    };

    // Wait for every <img> inside the PDF container to finish loading.
    // This is a safety net in case any image (e.g. data: URLs rendered by
    // SignatureInitialsImage after a state update) is not yet complete.
    const allImgs = Array.from(input.querySelectorAll("img"));
    await Promise.all(
      allImgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            }),
      ),
    );

    // Start from actual content width so the captured area is not padded with
    // whitespace.  A lower floor means less dead space → content fills more of the
    // PDF page width when scaled to 290 mm.
    let targetWidth = 0;
    const tables = input.querySelectorAll("table");
    tables.forEach((t) => {
      if (t.scrollWidth > targetWidth) targetWidth = t.scrollWidth;
    });
    targetWidth = Math.max(targetWidth + 40, 700); // 700 px minimum for short reports

    await html2canvas(input, {
      scale: 1,
      allowTaint: false,
      useCORS: true,
      backgroundColor: "#ffffff",
      width: targetWidth,
      windowWidth: targetWidth,
      logging: false,
      onclone: (clonedDoc) => {
        // Blank out every image that is OUTSIDE #marPDFReport so html2canvas
        // does not attempt CORS-gated S3 fetches for the visible modal table.
        const pdfTarget = clonedDoc.getElementById("marPDFReport");
        clonedDoc.querySelectorAll("img").forEach((img) => {
          if (pdfTarget && !pdfTarget.contains(img)) {
            img.src = "";
            img.removeAttribute("crossOrigin");
            img.removeAttribute("crossorigin");
          } else {
            img.style.display = "block";
          }
        });
        const clonedInput = clonedDoc.getElementById("marPDFReport");
        if (clonedInput) {
          clonedInput.style.width = "auto";
          clonedInput.style.minWidth = "fit-content";
          clonedInput.style.maxWidth = "none";
        }
        const scrollables = clonedDoc.querySelectorAll('[class*="tableBody"]');
        scrollables.forEach((s) => {
          s.style.overflow = "visible";
          s.style.overflowX = "visible";
          s.style.width = "auto";
          s.style.maxWidth = "none";
        });
        const divs = clonedDoc.querySelectorAll("div");
        divs.forEach((div) => {
          if (
            div.style.overflow === "scroll" ||
            div.style.overflowX === "scroll"
          ) {
            div.style.overflow = "visible";
            div.style.overflowX = "visible";
            div.style.width = "auto";
          }
        });
        const clonedTables = clonedDoc.querySelectorAll("table");
        clonedTables.forEach((table) => {
          table.style.width = "auto";
          table.style.minWidth = "fit-content";
          table.style.tableLayout = "auto";
        });
      },
    }).then(async (canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 290; // A4 landscape width in mm
      const pageHeight = 210; // A4 landscape height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // ── Section-aware pagination ─────────────────────────────────────────
      // For multi-month (yearly) reports, prefer page breaks at month section
      // boundaries so months are not cut in the middle of a table.
      // Each month Box has id="marSection-N"; we measure their offsetTop relative
      // to the forPdfBox container, convert to PDF mm, then prefer those positions
      // over the raw 210 mm page boundary when they fall within the current page.
      const sectionMmBreaks = [];
      const sectionEls = Array.from(
        input.querySelectorAll('[id^="marSection-"]'),
      );
      if (sectionEls.length > 1) {
        sectionEls.slice(1).forEach((el) => {
          let top = 0;
          let node = el;
          while (node && node !== input) {
            top += node.offsetTop;
            node = node.offsetParent;
          }
          // html2canvas scale=2 → canvas px = dom px × 2
          const canvasPx = top * 2;
          const mm = (canvasPx / canvas.height) * imgHeight;
          sectionMmBreaks.push(mm);
        });
      }

      const pdf = new jsPDF("landscape", "mm", "a4");
      const validImage = await loadImage(image);

      // Place the full tall image on page 1 (jsPDF clips to page height)
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.addImage(validImage, "PNG", 1, 1, 30, 30);

      // Walk through the image adding pages.  At each step, prefer a section
      // break over a raw page boundary if the section break falls before the
      // natural page end — this keeps month tables intact on the same page.
      let pageStart = 0; // mm from top of image where the current page begins
      while (pageStart + pageHeight < imgHeight) {
        const rawEnd = pageStart + pageHeight;

        // Find the nearest section break that falls INSIDE the current page window
        let nextCut = rawEnd;
        for (const sb of sectionMmBreaks) {
          if (sb > pageStart && sb < rawEnd) {
            nextCut = sb; // break at section boundary instead
            break;
          }
        }

        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, -nextCut, imgWidth, imgHeight);
        pageStart = nextCut;
      }
      // ─────────────────────────────────────────────────────────────────────

      pdf.save(`${reportName}.pdf`);
    });
  };

  return (
    <>
      <Modal
        centered
        size={1720}
        shadow="md"
        radius={"lg"}
        zIndex={1000}
        opened={opened}
        onClose={onClose}
        styles={{ header: { width: "1720px" } }}
        overlayProps={{ blur: 3 }}
      >
        <>
          <Box className={classes.exportBtn}>
            <div
              style={{
                opacity: loading ? 0.4 : 1,
                pointerEvents: loading ? "none" : "auto",
              }}
            >
              <ReactHTMLTableToExcel
                table="marTableExport"
                loading={loading}
                disabled={loading}
                sheet={reportName}
                id="test-table-xls-button"
                filename={`${reportName}`}
                className="download-table-xls-button"
                buttonText={
                  <img
                    src="/assets/images/xlsDowload.png"
                    alt="Export to Excel"
                  />
                }
              />
            </div>

            <ActionIcon
              ml={15}
              loading={pdfLoading || loading}
              disabled={loading}
              onClick={downloadPDF}
            >
              <img src="/assets/images/pdf.png" alt="" />
            </ActionIcon>
          </Box>
          <MarTableComponent
            image={image}
            loading={loading}
            daysArray={datesArray.map((d) => d.format("ddd"))}
            monthValue={monthValue}
            datesArray={datesArray}
            marTableData={marTableData}
            selectedClient={selectedClient}
            medicationStatus={medicationStatus}
            returnStaffLetters={returnStaffLetters}
            staffList={staffList}
            facilityName={facilityName}
            facilityPhoto={facilityPhoto}
          />

          <div style={{ display: "none" }}>
            <MarTableComponent
              image={image}
              loading={loading}
              daysArray={datesArray.map((d) => d.format("ddd"))}
              monthValue={monthValue}
              datesArray={datesArray}
              marTableData={marTableData}
              selectedClient={selectedClient}
              medicationStatus={medicationStatus}
              returnStaffLetters={returnStaffLetters}
              staffList={staffList}
              excelExportTable={true}
              facilityName={facilityName}
              facilityPhoto={facilityPhoto}
            />
          </div>
        </>
      </Modal>

      <PDFMarTableComponent
        image={image}
        loading={loading}
        datesArray={datesArray}
        monthValue={monthValue}
        marTableData={marTableData}
        selectedClient={selectedClient}
        medicationStatus={medicationStatus}
        returnStaffLetters={returnStaffLetters}
        staffList={staffList}
        allSignature={allSignature}
        pdfSignatures={pdfSignatures}
        isPDF={true}
        facilityName={facilityName}
        facilityPhoto={facilityPhoto}
      />
    </>
  );
};

const MarTableComponent = ({
  image,
  loading,
  daysArray,
  datesArray,
  monthValue,
  marTableData,
  selectedClient,
  medicationStatus,
  returnStaffLetters,
  staffList,
  excelExportTable = false,
  facilityName,
  facilityPhoto,
}) => {
  const { classes } = useStyles();
  const { allSignature } = useSelector((state) => state?.signature);

  const reportMonth = monthValue
    ?.map((item) =>
      moment(item).isValid() ? moment(item).format("MMM YYYY") : "",
    )
    .join(" - ");

  return (
    <Box className={classes.tableContainer}>
      {loading ? (
        <Box className={classes.loaderContainer}>
          <TableLoader />
        </Box>
      ) : (
        <>
          {/* ── Branded Header ── */}
          <div style={{ padding: "16px 20px 0 20px", fontFamily: BRAND.font }}>
            <ReportHeader
              facilityName={facilityName}
              facilityPhoto={facilityPhoto}
              reportTitle="Medication Administration Record (MAR)"
              reportSubtitle={`${reportMonth}  ·  Generated ${moment().format("MM/DD/YYYY")}`}
            />
            <ReportMetaGrid
              rows={[
                [
                  { label: "Client Name", value: selectedClient?.name },
                  {
                    label: "Date of Birth",
                    value: selectedClient?.form1?.dob
                      ? moment(selectedClient.form1.dob).format("MM/DD/YYYY")
                      : null,
                  },
                  { label: "Report Month", value: reportMonth },
                  { label: "Gender", value: selectedClient?.form1?.gender },
                ],
                [
                  {
                    label: "Known Allergies",
                    value:
                      selectedClient?.form1?.knownAllergies?.join(", ") ||
                      "None",
                  },
                  {
                    label: "Admission Date",
                    value:
                      selectedClient?.form1?.admissionDate ||
                      selectedClient?.form1?.admissiondate
                        ? moment(
                            selectedClient?.form1?.admissionDate ||
                              selectedClient?.form1?.admissiondate,
                          ).format("MM/DD/YYYY")
                        : null,
                  },
                  {
                    label: "Physician",
                    value:
                      selectedClient?.form1?.attendingPhysician ||
                      selectedClient?.form1?.physician,
                  },
                  {
                    label: "Diagnosis",
                    value:
                      selectedClient?.form1?.primaryDiagnosis ||
                      selectedClient?.form1?.diagnosis,
                  },
                ],
              ]}
            />

            {/* Staff Grid */}
            {staffList?.length > 0 && (
              <div
                style={{
                  ...BrandS.staffGrid,
                  gridTemplateColumns: "1fr",
                  marginBottom: 14,
                }}
              >
                <div>
                  <div
                    style={{
                      ...BrandS.staffHeader,
                      gridTemplateColumns: "1fr 80px 100px",
                    }}
                  >
                    <span>Staff Name</span>
                    <span>Initials</span>
                    <span>Signature</span>
                  </div>
                  {staffList.map((_s) => {
                    const sigObj = allSignature?.find(
                      ({ staff, admin }) =>
                        (staff && staff === _s.id) ||
                        (admin && admin === _s.id),
                    );
                    const initSrc = decrypt(sigObj?.signatureInitials);
                    const sigUrl =
                      decrypt(sigObj?.signature) ||
                      decrypt(sigObj?.signatureImage);
                    return (
                      <div
                        key={_s.id}
                        style={{
                          ...BrandS.staffRow,
                          gridTemplateColumns: "1fr 80px 100px",
                          borderBottom: `0.5px solid ${BRAND.lightBorder}`,
                        }}
                      >
                        <span style={{ fontSize: 11, fontWeight: 500 }}>
                          {_s.name}
                        </span>
                        <span style={{ fontSize: 11 }}>
                          {initSrc ? (
                            <img
                              src={initSrc}
                              width={60}
                              style={{ display: "block" }}
                            />
                          ) : sigObj?.signatureInitialsText ? (
                            <span
                              style={{
                                fontFamily: "Island Moments",
                                fontSize: 18,
                              }}
                            >
                              {sigObj.signatureInitialsText}
                            </span>
                          ) : (
                            _s.initial
                          )}
                        </span>
                        <span>
                          {sigUrl ? (
                            <img
                              src={sigUrl}
                              width={80}
                              style={{ display: "block" }}
                            />
                          ) : sigObj?.signatureText ? (
                            <span
                              style={{
                                fontFamily: "Island Moments",
                                fontSize: 20,
                              }}
                            >
                              {sigObj.signatureText}
                            </span>
                          ) : (
                            <span style={{ fontSize: 10, color: BRAND.muted }}>
                              —
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <Cuations />
          </div>
          <Box className={classes.tableBody}>
            <Table
              id={excelExportTable ? "marTableExport" : "marTable"}
              withColumnBorders
            >
              {/* Hidden thead for Excel formatting */}
              <thead style={{ display: "none" }}>
                <tr>
                  <td>
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={image}
                        width="50"
                        height="50"
                        style={{
                          display: "block",
                          objectFit: "cover",
                        }}
                        alt="logo"
                        crossOrigin="anonymous"
                      />
                    </div>
                  </td>
                  <td className={classes.dateCellsContent}></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td style={{ background: "#4fa80d" }}>
                    <Text fw={700} fz={14} w={200}>
                      Taken
                    </Text>
                  </td>
                  <td style={{ background: "#eb7a74" }}>
                    <Text fw={700} fz={14} w={200}>
                      Missed
                    </Text>
                  </td>
                  <td style={{ background: "#c7daf5" }}>
                    <Text fw={700} fz={14} w={200}>
                      Refused
                    </Text>
                  </td>
                  <td style={{ background: "#979797" }}>
                    <Text fw={700} fz={14} w={200}>
                      Upcoming
                    </Text>
                  </td>
                  <td style={{ background: "#feff80" }}>
                    <Text fw={700} fz={14} w={200}>
                      Unable to Take
                    </Text>
                  </td>
                  <td style={{ background: "#000000", color: "#ffffff" }}>
                    <Text fw={700} fz={14} w={200}>
                      Out of Medication
                    </Text>
                  </td>
                  <td style={{ background: "#8B4513", color: "#ffffff" }}>
                    <Text fw={700} fz={14} w={200}>
                      Discontinued
                    </Text>
                  </td>
                  <td style={{ background: "#a03399" }}>
                    <Text fw={700} fz={14} w={200}>
                      PRN
                    </Text>
                  </td>
                  <td></td>
                  <td>
                    <Avatar
                      w={50}
                      h={50}
                      alt="logo"
                      src={
                        "https://ogiso-bucket.s3.us-east-2.amazonaws.com/logo.png"
                      }
                      size={"50"}
                    ></Avatar>
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td style={{ background: "#b7b7b7" }}>
                    <Text fw={700} w={"100px"}>
                      Client Name:
                    </Text>
                  </td>
                  <td>
                    <Text w={"100px"}>{selectedClient?.name}</Text>{" "}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td style={{ background: "#b7b7b7" }}>
                    <Text fw={700}>Client DOB</Text>
                  </td>
                  <td>
                    {moment(selectedClient?.form1?.dob).format("MM/DD/YYYY")}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td style={{ background: "#b7b7b7" }}>
                    <Text fw={700}>Report Months</Text>
                  </td>
                  <td colSpan={3}>
                    {monthValue
                      ?.map((item) =>
                        moment(item).isValid()
                          ? moment(item).format("MMM YYYY")
                          : "",
                      )
                      .join(" - ")}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td style={{ background: "#b7b7b7" }}>
                    <Text fw={700}>Staff Name</Text>
                  </td>
                  {staffList?.map((_s) => (
                    <td colSpan={3} key={_s.id}>
                      {_s?.name}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td style={{ background: "#b7b7b7" }}>
                    <Text fw={700}>Staff Initials:</Text>
                  </td>
                  {staffList?.map((_s) => {
                    const signatureFind = allSignature?.find(
                      ({ staff, admin }) => {
                        const sId =
                          staff?._id?.toString() ||
                          staff?.id ||
                          (typeof staff === "string" ? staff : null);
                        const aId =
                          admin?._id?.toString() ||
                          admin?.id ||
                          (typeof admin === "string" ? admin : null);
                        return sId === _s.id || aId === _s.id;
                      },
                    );
                    return (
                      <td colSpan={3} key={_s.id}>
                        {decrypt(signatureFind?.signatureInitials) ? (
                          <img
                            src={decrypt(signatureFind?.signatureInitials)}
                            width={40}
                            height={20}
                            style={{
                              display: "block",
                              objectFit: "cover",
                              maxWidth: "100%",
                              maxHeight: "100%",
                            }}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : signatureFind?.signatureInitialsText ? (
                          <Text
                            fz={20}
                            fw={400}
                            ff="Island Moments"
                            color="#171717"
                          >
                            {signatureFind?.signatureInitialsText}
                          </Text>
                        ) : (
                          _s?.initial
                        )}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td colSpan={2}>
                    <Text fz={32} fw={700} w={400}>
                      {/* Special Healthcare LLC */}
                      {facilityName}
                    </Text>
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td colSpan={3}>
                    <Text fz={26} fw={600} w={500}>
                      Medication Administration Record (MAR)
                    </Text>
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td colSpan={3}>
                    <Text fz={20} fw={500} w={300}>
                      {monthValue
                        ?.map((item) =>
                          moment(item).isValid()
                            ? moment(item).format("MMM YYYY")
                            : "",
                        )
                        .join(" - ")}
                    </Text>
                  </td>
                </tr>
              </thead>

              <tbody>
                <tr
                  className={classes.dateCells}
                  style={{ background: "#d0e0e3" }}
                >
                  <td className={classes.dateCellsContent}></td>
                  <td className={classes.dateCellsContent}></td>
                  {daysArray?.map((day, index) => (
                    <td key={index} className={classes.dateCellsContent}>
                      <Text>{day}</Text>
                    </td>
                  ))}
                </tr>
                <tr
                  className={classes.dateCells}
                  style={{ background: "#d0e0e3" }}
                >
                  <td className={classes.dateCellsContent}></td>
                  <td className={classes.dateCellsContent}></td>
                  {datesArray?.map((date, index) => (
                    <td key={index} className={classes.dateCellsContent}>
                      <Text>{date?.format("MM/DD/YYYY")}</Text>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Medication</td>
                  <td>Time</td>
                  {datesArray?.map((date, index) => (
                    <td key={index}>
                      <Text>{date?.format("DD")}</Text>
                    </td>
                  ))}
                </tr>
                {marTableData?.length &&
                  marTableData?.map((marItem, marIndex) => (
                    <React.Fragment key={marIndex}>
                      {marIndex % 6 === 0 && (
                        <tr>
                          <td
                            rowSpan={7}
                            className={classes.bgTitleColor}
                            style={{
                              background: "#ffff00",
                              border: "1px solid #000000",
                            }}
                          >
                            <Box className={classes.medTitleCell}>
                              <Text fz={22} fw={600}>
                                {marItem?.medicine?.name}
                              </Text>
                              <Text>{marItem?.medicine?.instruction}</Text>
                            </Box>
                          </td>
                        </tr>
                      )}

                      <tr>
                        <td>
                          <Text w={"100px"}>{marItem?.slot}</Text>
                        </td>
                        {marItem?.data?.map((item, dataIdx) => {
                          const status = medicationStatus(item?.appointment);
                          return (
                            <td
                              key={dataIdx}
                              style={{
                                background:
                                  status == "missed"
                                    ? "#eb7a74"
                                    : status == "taken"
                                      ? "#4fa80d"
                                      : status == "discontinued"
                                        ? "#8B4513"
                                        : status == "out"
                                          ? "#000"
                                          : status == "refused"
                                            ? "#c7daf5"
                                            : status == "unable"
                                              ? "#feff80"
                                              : status == "prn"
                                                ? "#a03399"
                                                : "",
                                color: status == "out" ? "#fff" : "",
                              }}
                            >
                              {status === false ? (
                                ""
                              ) : status === "missed" ? (
                                "-"
                              ) : status === "refused" ? (
                                returnStaffLetters(
                                  item?.appointment,
                                  undefined,
                                  excelExportTable,
                                )
                              ) : status === "out" ? (
                                returnStaffLetters(
                                  item?.appointment,
                                  undefined,
                                  excelExportTable,
                                )
                              ) : status === "unable" ? (
                                returnStaffLetters(
                                  item?.appointment,
                                  undefined,
                                  excelExportTable,
                                )
                              ) : status === "taken" ? (
                                returnStaffLetters(
                                  item?.appointment,
                                  undefined,
                                  excelExportTable,
                                )
                              ) : status === "discontinued" ? (
                                <Text>D/C</Text>
                              ) : status === "prn" ? (
                                returnStaffLetters(
                                  item?.appointment,
                                  undefined,
                                  excelExportTable,
                                )
                              ) : (
                                ""
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    </React.Fragment>
                  ))}
              </tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
};

const PDFMarTableComponent = ({
  image,
  loading,
  datesArray,
  monthValue,
  marTableData,
  selectedClient,
  medicationStatus,
  returnStaffLetters,
  staffList,
  allSignature,
  pdfSignatures,
  isPDF,
  facilityName,
  facilityPhoto,
}) => {
  const { classes } = useStyles();

  const ref_months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  let startMonth = monthValue?.length
    ? ref_months?.findIndex(
        (item) =>
          item?.toLowerCase() ==
          moment(monthValue[0])?.format("MMM")?.toLowerCase(),
      )
    : -1;
  let endMonth = monthValue?.length
    ? ref_months?.findIndex(
        (item) =>
          item?.toLowerCase() ==
          moment(monthValue[monthValue?.length - 1])
            ?.format("MMM")
            ?.toLowerCase(),
      )
    : -1;

  let filteredMonths =
    startMonth >= 0 && endMonth >= 0
      ? ref_months?.slice(startMonth, endMonth + 1)
      : [];

  return (
    <Box className={classes.forPdfBox} id={"marPDFReport"}>
      {loading ? (
        <Box className={classes.loaderContainer}>
          <TableLoader />
        </Box>
      ) : (
        <>
          {/* ── Branded PDF Header ── */}
          <div style={{ padding: "28px 28px 0 28px", fontFamily: BRAND.font }}>
            <ReportHeader
              facilityName={facilityName}
              facilityPhoto={facilityPhoto}
              reportTitle="Medication Administration Record (MAR)"
              reportSubtitle={`${monthValue?.map((m) => (moment(m).isValid() ? moment(m).format("MMM YYYY") : "")).join(" - ")}  ·  Generated ${moment().format("MM/DD/YYYY")}`}
              forPdf
            />
            <ReportMetaGrid
              rows={[
                [
                  { label: "Client Name", value: selectedClient?.name },
                  {
                    label: "Date of Birth",
                    value: selectedClient?.form1?.dob
                      ? moment(selectedClient.form1.dob).format("MM/DD/YYYY")
                      : null,
                  },
                  {
                    label: "Report Month",
                    value: monthValue
                      ?.map((m) =>
                        moment(m).isValid() ? moment(m).format("MMM YYYY") : "",
                      )
                      .join(" - "),
                  },
                  { label: "Gender", value: selectedClient?.form1?.gender },
                ],
                [
                  {
                    label: "Known Allergies",
                    value:
                      selectedClient?.form1?.knownAllergies?.join(", ") ||
                      "None",
                  },
                  {
                    label: "Admission Date",
                    value:
                      selectedClient?.form1?.admissionDate ||
                      selectedClient?.form1?.admissiondate
                        ? moment(
                            selectedClient?.form1?.admissionDate ||
                              selectedClient?.form1?.admissiondate,
                          ).format("MM/DD/YYYY")
                        : null,
                  },
                  {
                    label: "Physician",
                    value:
                      selectedClient?.form1?.attendingPhysician ||
                      selectedClient?.form1?.physician,
                  },
                  {
                    label: "Diagnosis",
                    value:
                      selectedClient?.form1?.primaryDiagnosis ||
                      selectedClient?.form1?.diagnosis,
                  },
                ],
              ]}
            />
            {/* Staff Grid for PDF */}
            {staffList?.length > 0 && (
              <div
                style={{
                  border: `0.5px solid ${BRAND.lightBorder}`,
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 80px 100px",
                    background: BRAND.headerBg,
                    borderBottom: `0.5px solid ${BRAND.lightBorder}`,
                    padding: "4px 10px",
                    fontSize: 8,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    color: BRAND.muted,
                  }}
                >
                  <span>Staff Name</span>
                  <span>Initials</span>
                  <span>Signature</span>
                </div>
                {staffList.map((_s) => {
                  const sigObj = allSignature?.find(
                    ({ staff, admin }) =>
                      (staff && staff === _s.id) || (admin && admin === _s.id),
                  );
                  const initSrc = decrypt(sigObj?.signatureInitials);
                  const sigUrl =
                    decrypt(sigObj?.signature) ||
                    decrypt(sigObj?.signatureImage);
                  return (
                    <div
                      key={_s.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 80px 100px",
                        padding: "5px 10px",
                        alignItems: "center",
                        borderBottom: `0.5px solid ${BRAND.lightBorder}`,
                      }}
                    >
                      <span style={{ fontSize: 11, fontWeight: 500 }}>
                        {_s.name}
                      </span>
                      <span style={{ fontSize: 11 }}>
                        {initSrc ? (
                          <img
                            src={initSrc}
                            width={60}
                            crossOrigin="anonymous"
                            style={{ display: "block" }}
                            alt="Initials"
                          />
                        ) : sigObj?.signatureInitialsText ? (
                          <span
                            style={{
                              fontFamily: "Island Moments",
                              fontSize: 18,
                            }}
                          >
                            {sigObj.signatureInitialsText}
                          </span>
                        ) : (
                          _s.initial
                        )}
                      </span>
                      <span>
                        {sigUrl ? (
                          <img
                            src={sigUrl}
                            width={80}
                            crossOrigin="anonymous"
                            style={{ display: "block" }}
                            alt="Signature"
                          />
                        ) : sigObj?.signatureText ? (
                          <span
                            style={{
                              fontFamily: "Island Moments",
                              fontSize: 20,
                            }}
                          >
                            {sigObj.signatureText}
                          </span>
                        ) : (
                          <span style={{ fontSize: 10, color: BRAND.muted }}>
                            —
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            <Cuations />
          </div>
          {filteredMonths?.map((month, monthIndex) => (
            <Box key={monthIndex} id={`marSection-${monthIndex}`} mb={30}>
              <Box
                className={classes.reportMonth}
                sx={{ paddingRight: monthIndex == 0 ? "calc(10% + 50px)" : 0 }}
              >
                <Text fw={700}>Report Month: </Text>
                <Text fz={20} fw={500} align="center">
                  {month}
                </Text>
              </Box>

              {/* Table Body */}
              <Box className={classes.tableBody}>
                <Table id={`marTable-${monthIndex}`} withColumnBorders>
                  <thead>
                    <tr
                      className={classes.dateCells}
                      style={{ background: "#d0e0e3" }}
                    >
                      <td className={classes.dateCellsContent}></td>
                      <td className={classes.dateCellsContent}></td>
                      {datesArray
                        ?.filter(
                          (item) =>
                            moment(item)?.format("MMM")?.toLowerCase() ==
                            month?.toLowerCase(),
                        )
                        ?.map((date, index) => (
                          <td key={index} className={classes.dateCellsContent}>
                            <Text>{date.format("ddd")}</Text>
                          </td>
                        ))}
                    </tr>
                    <tr
                      className={classes.dateCells}
                      style={{ background: "#d0e0e3" }}
                    >
                      <td className={classes.dateCellsContent}></td>
                      <td className={classes.dateCellsContent}></td>
                      {datesArray
                        ?.filter(
                          (item) =>
                            moment(item)?.format("MMM")?.toLowerCase() ==
                            month?.toLowerCase(),
                        )
                        ?.map((date, index) => (
                          <td key={index} className={classes.dateCellsContent}>
                            <Text>{date.format("MM/DD/YYYY")}</Text>
                          </td>
                        ))}
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>Medication</td>
                      <td>Time</td>
                      {datesArray
                        ?.filter(
                          (item) =>
                            moment(item)?.format("MMM")?.toLowerCase() ==
                            month?.toLowerCase(),
                        )
                        ?.map((date, index) => (
                          <td key={index} className={classes.dateCellsContent}>
                            <Text>{index + 1}</Text>
                          </td>
                        ))}
                    </tr>

                    {marTableData?.map((marItem, marIndex) => (
                      <React.Fragment key={marIndex}>
                        {marIndex % 6 === 0 && (
                          <tr>
                            <td
                              rowSpan={7}
                              className={classes.bgTitleColor}
                              style={{
                                background: "#ffff00",
                                border: "1px solid #000000",
                              }}
                            >
                              <Box className={classes.medTitleCell}>
                                <Text fz={24} fw={600}>
                                  {marItem?.medicine?.name}
                                </Text>
                                <Text fz={14}>
                                  {marItem?.medicine?.instruction}
                                </Text>
                              </Box>
                            </td>
                          </tr>
                        )}

                        <tr>
                          <td>
                            <Text fz={13} w={"100px"}>
                              {marItem?.slot}
                            </Text>
                          </td>
                          {marItem?.data
                            ?.filter(
                              (item) =>
                                moment(item?.date)
                                  ?.format("MMM")
                                  ?.toLowerCase() == month?.toLowerCase(),
                            )
                            ?.map((item, dataIdx) => {
                              const status = medicationStatus(
                                item?.appointment,
                              );
                              return (
                                <td
                                  key={dataIdx}
                                  style={{
                                    background:
                                      status === "missed"
                                        ? "#eb7a74"
                                        : status === "taken"
                                          ? "#4fa80d"
                                          : status === "out"
                                            ? "#000"
                                            : status === "refused"
                                              ? "#c7daf5"
                                              : status === "unable"
                                                ? "#feff80"
                                                : status === "discontinued"
                                                  ? "#8B4513"
                                                  : status === "prn"
                                                    ? "#a03399"
                                                    : "",
                                    color: status === "out" ? "#fff" : "",
                                  }}
                                >
                                  {status === false ? (
                                    ""
                                  ) : status === "missed" ? (
                                    "-"
                                  ) : status === "refused" ? (
                                    returnStaffLetters(item?.appointment, isPDF)
                                  ) : status === "out" ? (
                                    returnStaffLetters(item?.appointment, isPDF)
                                  ) : status === "unable" ? (
                                    returnStaffLetters(item?.appointment, isPDF)
                                  ) : status === "taken" ? (
                                    returnStaffLetters(item?.appointment, isPDF)
                                  ) : status === "discontinued" ? (
                                    <Text>D/C</Text>
                                  ) : status === "prn" ? (
                                    returnStaffLetters(item?.appointment, isPDF)
                                  ) : (
                                    ""
                                  )}
                                </td>
                              );
                            })}
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </Box>
            </Box>
          ))}
          {/* ── Branded Footer ── */}
          <div style={{ padding: "0 28px 16px 28px" }}>
            <ReportFooter facilityName={facilityName} />
          </div>
        </>
      )}
    </Box>
  );
};

const useStyles = createStyles((theme) => ({
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "70vh",
  },
  reportMonth: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "1rem",
  },
  greenBox: {
    width: "15px",
    height: "15px",
    background: "#4fa80d",
  },
  exportBtn: {
    display: "flex",
    justifyContent: "end",
    padding: "1rem",
    alignItems: "center",
    gap: "1rem",
    width: "1700px",
  },
  tableContainer: {
    border: "1px solid #EDEDED",
    width: "1700px",
  },
  forPdfBox: {
    width: "1920px",
    position: "absolute",
    left: "-9999px",
  },
  clientDetails: {
    display: "flex",
    alignItems: "start",
    gap: ".5rem",
    justifyContent: "start",
    padding: "1rem",
  },
  cuationsBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: ".5rem",
    flexDirection: "column",
  },
  cuationBoxPDF: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "1rem",
  },
  signatureBoxPDF: {
    display: "flex",
    alignItems: "start",
    gap: ".5rem",
    justifyContent: "start",
    flexDirection: "column",
  },
  marTextLogoBoxPDF: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "1rem",
  },
  tableBodyBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "start",
    gap: ".5rem",
    width: "100%",
  },
  bgCellColor: {
    backgroundColor: "#b7b7b7",
  },
  dateCells: {
    backgroundColor: "#d0e0e3",
    border: "unset",
  },
  bgTitleColor: {
    background: "#ffff00",
    width: "200px",
  },
  medTitleCell: {
    width: "200px",
  },
  tableHeader: {
    width: "100%",
  },
  tableBody: {
    overflowX: "scroll",
    "& table": {
      width: "500px",
      "& tr:nth-child(1) td": {
        fontSize: "14px !important",
        fontWeight: "500 !important",
        textAlign: "center",
      },
      "& tr:nth-child(2) td": {
        fontSize: "12px !important",
        fontWeight: "400 !important",
        textAlign: "center",
      },
    },
  },
  dateCellsContent: {
    borderTop: "none !important",
    borderRight: "none !important",
  },
}));

export default MarViewReportModal;
